import { useEffect, useLayoutEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useIsFetching } from '@tanstack/react-query';
import {
  isSnapshotRouteReady,
  type PageProgressSnapshot,
  usePageProgressSnapshot,
  usePageProgressStore,
} from '../../contexts/PageProgressContext';
import {
  notifyParentProgressDone,
  notifyParentProgressStart,
} from '../../util/parentShell';

/** Pages that declare readiness explicitly can finish as soon as they say so. */
const REGISTERED_SETTLE_MS = 150;
/**
 * Pages with no explicit registration must wait longer: a page that will
 * register may not have reached its layout effect yet, and finishing early is
 * exactly the bug this guards against.
 */
const UNREGISTERED_SETTLE_MS = 600;
const MAX_UNREGISTERED_HOLD_MS = 12_000;

/** True when this route owns content readiness (e.g. opinion-compare). */
function hasContentRegistration(snapshot: PageProgressSnapshot): boolean {
  return snapshot.registrationCount > 0;
}

/**
 * Decide whether the shell bar must stay up.
 * Registered pages (compare) finish when they say ready — never global RQ busy.
 */
function shouldHoldShellProgress(
  snapshot: PageProgressSnapshot,
  queryBusy: number
): boolean {
  if (!isSnapshotRouteReady(snapshot)) {
    return true;
  }
  if (hasContentRegistration(snapshot)) {
    return false;
  }
  return queryBusy > 0;
}

/**
 * Embedded-only: keeps the V3 shell top progress bar alive until this route's
 * lazy chunk, React Query work, and registered page content are all ready.
 *
 * Must be rendered after <Routes> so page registrations land before this
 * component's completion layout effect runs.
 */
function V2ProgressReporter(): null {
  const location = useLocation();
  const store = usePageProgressStore();
  const snapshot = usePageProgressSnapshot();
  const pendingFetchCount = useIsFetching({
    predicate: (query) =>
      query.state.status === 'pending' &&
      query.state.fetchStatus === 'fetching',
  });
  const queryBusy = pendingFetchCount;
  const queryBusyRef = useRef(queryBusy);
  const sessionActiveRef = useRef(false);
  const quietTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const maxHoldTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  queryBusyRef.current = queryBusy;

  useLayoutEffect(() => {
    if (quietTimerRef.current !== null) {
      clearTimeout(quietTimerRef.current);
      quietTimerRef.current = null;
    }
    if (maxHoldTimerRef.current !== null) {
      clearTimeout(maxHoldTimerRef.current);
      maxHoldTimerRef.current = null;
    }

    notifyParentProgressStart();
    if (sessionActiveRef.current) {
      notifyParentProgressDone();
    }
    sessionActiveRef.current = true;
  }, [location.pathname, location.search]);

  useEffect(
    () => () => {
      if (quietTimerRef.current !== null) {
        clearTimeout(quietTimerRef.current);
      }
      if (maxHoldTimerRef.current !== null) {
        clearTimeout(maxHoldTimerRef.current);
      }
      if (sessionActiveRef.current) {
        notifyParentProgressDone();
        sessionActiveRef.current = false;
      }
    },
    []
  );

  useLayoutEffect(() => {
    const current = store.getSnapshot();
    const routeReady = isSnapshotRouteReady(current);
    const hasRegistration = hasContentRegistration(current);

    if (
      routeReady &&
      !hasRegistration &&
      maxHoldTimerRef.current === null &&
      sessionActiveRef.current
    ) {
      maxHoldTimerRef.current = setTimeout(() => {
        maxHoldTimerRef.current = null;
        if (!sessionActiveRef.current) return;

        const latest = store.getSnapshot();
        if (
          !isSnapshotRouteReady(latest) ||
          hasContentRegistration(latest)
        ) {
          return;
        }

        if (quietTimerRef.current !== null) {
          clearTimeout(quietTimerRef.current);
          quietTimerRef.current = null;
        }
        notifyParentProgressDone();
        sessionActiveRef.current = false;
      }, MAX_UNREGISTERED_HOLD_MS);
    } else if ((!routeReady || hasRegistration) && maxHoldTimerRef.current) {
      clearTimeout(maxHoldTimerRef.current);
      maxHoldTimerRef.current = null;
    }

    if (shouldHoldShellProgress(current, queryBusy)) {
      if (quietTimerRef.current !== null) {
        clearTimeout(quietTimerRef.current);
        quietTimerRef.current = null;
      }
      if (!sessionActiveRef.current) {
        notifyParentProgressStart();
        sessionActiveRef.current = true;
      }
      return;
    }

    if (!sessionActiveRef.current) return;
    if (quietTimerRef.current !== null) return;

    const settleMs = hasContentRegistration(current)
      ? REGISTERED_SETTLE_MS
      : UNREGISTERED_SETTLE_MS;

    quietTimerRef.current = setTimeout(() => {
      quietTimerRef.current = null;
      if (!sessionActiveRef.current) return;

      const latest = store.getSnapshot();
      const busyForCheck = hasContentRegistration(latest)
        ? 0
        : queryBusyRef.current;
      if (shouldHoldShellProgress(latest, busyForCheck)) return;

      if (maxHoldTimerRef.current !== null) {
        clearTimeout(maxHoldTimerRef.current);
        maxHoldTimerRef.current = null;
      }
      notifyParentProgressDone();
      sessionActiveRef.current = false;
    }, settleMs);
  }, [queryBusy, snapshot, store, location.pathname, location.search]);

  return null;
}

export default V2ProgressReporter;
